import { AlertTriangle, AlertCircle, Lightbulb, CheckCircle2, X } from 'lucide-react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import type { Insight } from '../../api/client';
import { formatDateTime } from '../../utils/formatters';

interface InsightDetailProps {
  insight: Insight | null;
  onClose: () => void;
  onDismiss: (id: string) => void;
  onResolve: (id: string) => void;
}

const categoryIcons = {
  critical: AlertTriangle,
  warning: AlertCircle,
  opportunity: Lightbulb,
  positive: CheckCircle2,
};

const categoryBadgeStyles = {
  critical: 'bg-red-100 text-red-700',
  warning: 'bg-yellow-100 text-yellow-700',
  opportunity: 'bg-blue-100 text-blue-700',
  positive: 'bg-green-100 text-green-700',
};

function impactScoreColor(score: number): string {
  if (score >= 80) return 'bg-red-100 text-red-700 border-red-200';
  if (score >= 50) return 'bg-orange-100 text-orange-700 border-orange-200';
  if (score >= 20) return 'bg-blue-100 text-blue-700 border-blue-200';
  return 'bg-green-100 text-green-700 border-green-200';
}

export default function InsightDetail({ insight, onClose, onDismiss, onResolve }: InsightDetailProps) {
  if (!insight) return null;

  const CategoryIcon = categoryIcons[insight.category] || AlertCircle;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-200 p-6">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <CategoryIcon className={clsx('h-5 w-5', categoryBadgeStyles[insight.category]?.split(' ')[1])} />
              <span className={clsx('rounded-full px-2 py-0.5 text-xs font-medium', categoryBadgeStyles[insight.category])}>
                {insight.category}
              </span>
              <span
                className={clsx(
                  'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-bold',
                  impactScoreColor(insight.impactScore),
                )}
              >
                Impact: {insight.impactScore}
              </span>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{insight.title}</h2>
          </div>
          <button onClick={onClose} className="ml-4 shrink-0 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-6 p-6">
          {/* Description */}
          <div>
            <h3 className="mb-1 text-sm font-semibold text-gray-900">Description</h3>
            <p className="text-sm text-gray-600">{insight.description}</p>
          </div>

          {/* Evidence */}
          {insight.evidence && Object.keys(insight.evidence).length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-gray-900">Evidence</h3>
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <tbody className="divide-y divide-gray-200">
                    {Object.entries(insight.evidence).map(([key, value]) => (
                      <tr key={key}>
                        <td className="whitespace-nowrap px-4 py-2 text-sm font-medium text-gray-700">{key}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">
                          {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Affected Pages */}
          {insight.affectedPages && insight.affectedPages.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-gray-900">Affected Pages</h3>
              <div className="space-y-1">
                {insight.affectedPages.map((page) => (
                  <Link
                    key={page}
                    to={`/heatmaps?url=${encodeURIComponent(page)}`}
                    className="block rounded-lg border border-gray-100 px-3 py-2 text-sm text-brand-600 hover:bg-gray-50"
                  >
                    {page}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Affected Elements */}
          {insight.affectedElements && insight.affectedElements.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-gray-900">Affected Elements</h3>
              <div className="space-y-1">
                {insight.affectedElements.map((el) => (
                  <div key={el} className="rounded-lg border border-gray-100 px-3 py-2">
                    <code className="text-sm text-gray-700">{el}</code>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Created At */}
          <div>
            <h3 className="mb-1 text-sm font-semibold text-gray-900">Created</h3>
            <p className="text-sm text-gray-600">{formatDateTime(insight.createdAt)}</p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <button
            onClick={() => { onDismiss(insight.id); onClose(); }}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Dismiss
          </button>
          <button
            onClick={() => { onResolve(insight.id); onClose(); }}
            className="rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-100"
          >
            Resolve
          </button>
          <button
            onClick={onClose}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
